import { observable, action, computed, reaction } from 'mobx';
import { isFunction } from 'lodash';
import { api } from '../services';
import { filterStore } from 'air-filter-mobx-store';
import { commonStore, translationsStore } from './index';
import moment from 'moment';
import isArray from 'lodash/isArray';

class SubscriptionsStore {
  @observable vehicles = [];
  @observable loadedAt;
  @observable err;

  constructor(filterStore) {
    this.filterStore = filterStore;
    this.commonStore = commonStore;
    this.translationsStore = translationsStore;

    reaction(
      () => this.filterStore.globalFilterFunction,
      fn => {
        const vehicles = this.vehicles.map(vehicle => {
          if (fn(vehicle)) {
            return { ...vehicle, hidden: false };
          } else {
            return { ...vehicle, hidden: true };
          }
        });
        this.setVehicles(vehicles);
      }
    );
  }

  @action
  async load() {
    this.commonStore.loading = true;

    if (!this.commonStore.dealer) {
      await api('dealer profile').then(dealer => {
        commonStore.setDealer(dealer);
      });
    }

    return api('getSubscriptions', this.commonStore.dealer.masterApiUserUuid)
      .then(results => {
        const directManageableContractsPaid = results.filter(contract => contract.status !== 'unpaid'),
          contractsHavingBaseProducts = directManageableContractsPaid.filter(contract => contract.product.category === 'base');
        let vehicles = [];
        contractsHavingBaseProducts.forEach((contract, index) => {

          // INFO contract.vehicle, contract.installation from API are arrays
          let contractActiveVehicle = contract.vehicle.find(vehicle => vehicle.till === undefined || (vehicle.hasOwnProperty('till') && moment().isBefore(vehicle.till)));
          let contractActiveVehicleInstaller = contractActiveVehicle ? contractActiveVehicle.installer : undefined;
          contractActiveVehicle = contractActiveVehicle ? contractActiveVehicle.node : undefined;
          let contractActiveInstallation = contract.installation.find(
            installation => installation.till === undefined || (installation.hasOwnProperty('till') && moment().isBefore(installation.till))
          );

          let managementStatus = 'created',
            managementStatusDate = moment(contract.start_date).format('YYYY-MM-DD');
          if (contractActiveVehicle !== undefined && contractActiveInstallation === undefined) {
            managementStatus = 'tobeconnected';
            const lastVehicle = contract.vehicle
              .sort((a, b) => (a.since > b.since ? 1 : b.since > a.since ? -1 : 0))
              .slice()
              .pop();
            managementStatusDate = moment(lastVehicle.since).format('YYYY-MM-DD');
          }
          if (contractActiveVehicle !== undefined && contractActiveInstallation !== undefined) {
            managementStatus = 'connected';
            const lastInstallation = contract.installation
              .sort((a, b) => (a.since > b.since ? 1 : b.since > a.since ? -1 : 0))
              .slice()
              .pop();
            managementStatusDate = moment(lastInstallation.since).format('YYYY-MM-DD');
          }
          if (
            contractActiveVehicle !== undefined &&
            contractActiveInstallation !== undefined &&
            contractActiveInstallation.node &&
            contractActiveInstallation.node.hasOwnProperty('disconnection_request_date') &&
            moment().isAfter(contractActiveInstallation.node.disconnection_request_date)
          ) {
            managementStatus = 'tobedisconnected';
            managementStatusDate = moment(contractActiveInstallation.node.disconnection_request_date).format('YYYY-MM-DD');
          }
          if (contractActiveVehicle === undefined && contractActiveInstallation === undefined && isArray(contract.vehicle) && contract.vehicle.length > 0) {
            managementStatus = 'disconnected';
            const lastInstallation = contract.installation
              .sort((a, b) => (a.since > b.since ? 1 : b.since > a.since ? -1 : 0))
              .slice()
              .pop();
            managementStatusDate = moment((lastInstallation || {}).till).format('YYYY-MM-DD');
          }

          const vehicleStatusHistory = [{ status: 'created', statusDate: contract.start_date }];
          contract.vehicle.forEach((vehicle, index) => {
            const installation = contract.installation[index];
            if (vehicle && vehicle.since) {
              vehicleStatusHistory.push({ status: 'tobeconnected', statusDate: moment(vehicle.since).format('YYYY-MM-DD') });
            }
            if (installation && installation.since) {
              vehicleStatusHistory.push({ status: 'connected', statusDate: moment(installation.since).format('YYYY-MM-DD') });
            }
            if (installation && installation.since && installation.node && installation.node.disconnection_request_date) {
              vehicleStatusHistory.push({ status: 'tobedisconnected', statusDate: moment(installation.node.disconnection_request_date).format('YYYY-MM-DD') });
            }
            if (installation && installation.till) {
              vehicleStatusHistory.push({ status: 'disconnected', statusDate: moment(installation.till).format('YYYY-MM-DD') });
            }
          });

          if(managementStatus === 'disconnected') {
            //TODO change name from contractActiveVehicle to something more meaningful for disconnected vehicle for portability
            contractActiveVehicle = contract.vehicle.find(vehicle => vehicle.hasOwnProperty('till'));
            contractActiveVehicleInstaller = contractActiveVehicle ? contractActiveVehicle.installer : undefined;
            contractActiveVehicle = contractActiveVehicle ? contractActiveVehicle.node : undefined;
            contractActiveInstallation = contract.installation.find(
              installation => installation.hasOwnProperty('till')
            );
          }

          vehicles.push({
            ...contract,
            products: [contract.product],
            activeVehicle: contractActiveVehicle,
            activeVehicleInstaller: contractActiveVehicleInstaller,
            activeInstallation: contractActiveInstallation,
            vehicles: contract.vehicle,
            installations: contract.installation,
            vehicleStatusHistory,
            managementStatus,
            managementStatusDate
          });
        });
        const contractsHavingNonBaseProducts = directManageableContractsPaid.filter(contract => contract.product.category !== 'base');
        contractsHavingNonBaseProducts.forEach((contract, index) => {
          const subscriptionIndex = vehicles.findIndex(
            subscription => subscription.activeVehicle && contract.vehicle.map(vehicle => vehicle.node.license_plate).includes(subscription.activeVehicle.license_plate)
          );
          if (subscriptionIndex !== -1) {
            vehicles[subscriptionIndex].products.push(contract.product);
          }
        });
        vehicles = vehicles.sort((a, b) => (a.start_date > b.start_date ? 1 : b.start_date > a.start_date ? -1 : 0));
        this.setVehicles(vehicles);
        this.setLoadedAt();
      })
      .catch(error => this.err = error)
      .finally(() => (this.commonStore.loading = false));
  }

  @action setLoadedAt() {
    this.loadedAt = new Date().getTime();
  }

  @action
  setVehicles(vehicles) {
    this.vehicles = vehicles;
  }

  @computed get vehiclesWithStatusConnected() {
    return this.vehicles.filter(vehicle => vehicle.managementStatus === 'connected');
  }

  @computed get filtered() {
    if (this.filterStore.globalFilterFunction) {
      return this.vehicles.filter(this.filterStore.globalFilterFunction);
    } else {
      return this.vehicles;
    }
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };

  groupsLength = (filterType, attribute) => {
    if (attribute === 'all' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.vehicles.filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'tobeconnected' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.vehicles.filter(vehicle => vehicle.managementStatus === 'tobeconnected').filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'connected' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.vehicles.filter(vehicle => vehicle.managementStatus === 'connected').filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'tobedisconnected' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.vehicles.filter(vehicle => vehicle.managementStatus === 'tobedisconnected').filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'disconnected' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.vehicles.filter(vehicle => vehicle.managementStatus === 'disconnected').filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
  };

  textSearchFns = searchText => subscription => {
    if (!searchText || searchText.length < 1) {
      return true;
    }
    if (subscription.vehicles && subscription.vehicles.length > 0 && subscription.vehicles.map(el => el.node.license_plate).some(el => el.toLowerCase().includes(searchText.toLowerCase()))) {
      return true;
    }
    if (subscription.products.map(el => el.name).some(el => el.toLowerCase().includes(searchText.toLowerCase()))) {
      return true;
    }
    if (
      subscription.managementStatusDate &&
      new Date(subscription.managementStatusDate).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }).includes(searchText)
    ) {
      return true;
    }
    if (
      subscription.managementStatus &&
      translationsStore
        .translate(`vehiclesManager.status.${subscription.managementStatus}`)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    ) {
      return true;
    }
    if (subscription.installations && subscription.installations.map(el => el.node.asset.ain).some(el => el.toLowerCase().includes(searchText.toLowerCase()))) {
      return true;
    }
    return false;
  };

  @action
  async createDisconnection(subscription) {
    this.commonStore.loading = true;
    await api('createDisconnection', this.commonStore.dealer.masterApiUserUuid, subscription.uuid).finally(() => (this.commonStore.loading = false))
      .catch(err => this.err = err);
    await this.load();
  }

  @action
  async removeDisconnection(subscription) {
    this.commonStore.loading = true;
    await api('removeDisconnection', this.commonStore.dealer.masterApiUserUuid, subscription.uuid).finally(() => (this.commonStore.loading = false))
      .catch(err => this.err = err);
    await this.load();
  }
}

export default new SubscriptionsStore(filterStore, commonStore, translationsStore);



// WEBPACK FOOTER //
// ./src/stores/fleetCustomerContractsStore.js